import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type Customer, customerFragment } from "./customers";

export type Page = {
  slug: string;
  eyebrow: string;
  title: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  metaImageUrl?: string;
  customers: Customer[];
};

export const getPage = async (slug: string): Promise<Page | undefined> => {
  const query = groq`
    *[_type == "page" && slug == $slug][0] {
      slug,
      eyebrow,
      title,
      description,
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url,
      customers[]->{ ${customerFragment} }
    }
  `;

  try {
    const parameters = { slug };
    const pageResult = await getClient().fetch<Page | undefined>(query, parameters);
    return pageResult;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch page with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};

export type AdsPage = {
  slug: string;
  template: "home" | "ai-decisioning" | "composable-cdp";
  eyebrow?: string;
  title: string;
  description: string;
  imageUrl?: string;
  mediaType?: "image" | "demo" | "cdp-animation";
  demoTitle?: string;
  isDemoExpanded?: boolean;
  content?: string;
  chilipiperRouter?: string;
  metaTitle: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const getAdsPage = async (slug: string): Promise<AdsPage | undefined> => {
  const query = groq`
    *[_type == "adsPage" && slug == $slug][0] {
      slug,
      template,
      eyebrow,
      title,
      description,
      'imageUrl': image.asset->url,
      mediaType,
      demoTitle,
      isDemoExpanded,
      content,
      'chilipiperRouter': coalesce(chilipiperRouter, "inbound-router"),
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url
    }
  `;

  try {
    const parameters = { slug };
    const adsPage = await getClient().fetch<AdsPage | undefined>(query, parameters);
    return adsPage;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch ads page with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};
